import { PostAxios, GetAxios, PatchAxios } from "../helpers/axiosHelpers";

/**
 * Sanitize input to prevent XSS attacks
 * @param {string} input - User input
 * @returns {string} - Sanitized input
 */
const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;
  return input.trim().replace(/[<>"'&]/g, (char) => {
    const entities = {
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
      "&": "&amp;",
    };
    return entities[char] || char;
  });
};

/**
 * Login API call with security measures
 * @param {object} credentials - Login credentials { email, password }
 * @returns {Promise} - Login response with user data and token
 */
export const loginAPI = async (credentials) => {
  try {
    // Sanitize inputs before sending
    const sanitizedCredentials = {
      email: sanitizeInput(credentials.email.toLowerCase()),
      password: credentials.password, // Don't sanitize password, backend handles it
    };

    const response = await PostAxios("/auth/login", sanitizedCredentials);
    return response;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Login failed");
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Get user profile API call
 * @returns {Promise} - User profile data
 */
export const getProfileAPI = async () => {
  try {
    const response = await GetAxios("/auth/profile");
    return response;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to fetch profile");
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Change password API call
 * @param {string} userId - User ID
 * @param {object} passwordData - Password change data { currentPassword, newPassword }
 * @returns {Promise} - Success response
 */
export const changePasswordAPI = async (userId, passwordData) => {
  try {
    const response = await PatchAxios(
      `/adminuser/${userId}/change-password`,
      passwordData,
    );
    return response;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.message || "Failed to change password",
      );
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Logout - Call backend to clear httpOnly cookie
 */
export const logoutAPI = async () => {
  try {
    // Backend will clear the httpOnly cookie
    await PostAxios("/auth/logout");
  } catch (error) {
    console.error("Logout error:", error);
    // Continue with client-side cleanup even if API fails
  }

  // Clear sessionStorage
  sessionStorage.clear();
};
